import React, {useState} from 'react';

import moment from 'moment-tz';

import {AccountPageShell} from 'app/account/components/AccountPageShell';
import {useCurrentUserState} from 'app/current-user/api';
import {useCodes, useReferrals} from 'app/referrals/api';
import {ACCOUNT_REFERRALS_FORM_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {postData} from 'app/utils/request';

import type {
  ListResponseType,
  ReferralSerializedType,
  WebinarCodeSerializedType,
} from 'sweet-types';

export function ReferralsPage(): JSX.Element {
  const {data: codes} = useCodes();
  const {data: referrals, error, isLoading} = useReferrals();

  return (
    <AccountPageShell
      title={gt('Referrals')}
      location="referrer"
      isLoading={isLoading}
      error={error}
      content={
        (codes?.count ?? (0 || referrals?.count) ?? 0) > 0
          ? {codes, referrals}
          : undefined
      }
      errorContent={{
        head: gt('Could not load referral information at the moment'),
      }}
      noResultsContent={<JoinReferralProgram />}
    >
      {(data) => (
        <>
          {data.codes != null ? <CodeCards codes={data.codes} /> : null}
          {data.referrals != null ? (
            <Referrals referrals={data.referrals} />
          ) : null}
        </>
      )}
    </AccountPageShell>
  );
}

type Props = {
  codes: ListResponseType<WebinarCodeSerializedType>;
};

const CodeCards = ({codes}: Props) => {
  return (
    <div>
      <h3>{gt('My Referral Codes')}</h3>
      {codes.results.map((code) => (
        <div className="card mb-3 col-4" key={code.url}>
          <div className="card-body">
            <h5 className="card-title">{code.code}</h5>
            <p className="card-text">
              Trial Expires in {code.trial_expires_days} days
            </p>
            <span className="card-text d-block">{code.url}</span>
            <span className="card-text">{code.short_url}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

type ReferralProps = {
  referrals: ListResponseType<ReferralSerializedType>;
};

const Referrals = ({referrals}: ReferralProps) => (
  <>
    <h3>{gt('Referrals tracked')}</h3>
    <table className="table">
      <thead className="thead-light">
        <tr>
          <th>{gt('Code')}</th>
          <th>{gt('Name')}</th>
          <th>{gt('Signed Up')}</th>
          <th>{gt('Commission Total ($)')}</th>
          <th>{gt('Paid Out ($)')}</th>
        </tr>
      </thead>
      <tbody>
        {referrals.results.map((referral) => (
          <tr key={referral.signed_up}>
            <td>{referral.code}</td>
            <td>{referral.name}</td>
            <td>{moment(referral.signed_up).format('lll')}</td>
            <td>{(referral.commission_cents / 100).toFixed(2)}</td>
            <td>{(referral.paid_cents / 100).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

const JoinReferralProgram = () => {
  const {data} = useCurrentUserState();

  const [body, setBody] = useState('');
  const [isSent, setIsSent] = useState<boolean>(false);

  const submitReferralRequest = (data) =>
    postData(ACCOUNT_REFERRALS_FORM_URL, data).then(() => setIsSent(true));
  return (
    <div className="row text-left">
      <div className="col-8">
        <h3>{gt('SweetProcess Referral Program')}</h3>
        {isSent && <ReferralSuccessMessage />}
        <p className="mt-4">
          {gt(
            'Do you use SweetProcess and know a manager or an executive at a company with '
          )}
          <span className="font-weight-bold">
            {gt('30 or more employees who might benefit from using it too?')}
          </span>
        </p>
        <p>{gt('OR')}</p>
        <p>
          {gt('Do you consult with companies with ')}
          <span className="font-weight-bold">{gt('30 or more employees')}</span>
          {gt(
            ' and help them document their procedures or do you help them to systematize their operations?'
          )}
        </p>
        <p>
          {gt(
            'Do you meet any of the conditions above and want to earn a referral fee for recommending SweetProcess?'
          )}
        </p>
        <p>
          {gt(
            'The SweetProcess Referral Program will allow you to do just that.'
          )}
        </p>
        <p>
          {gt(
            'Fill out the form below to signup for our referral program and you will be sent additional instructions.'
          )}
        </p>

        {isSent === false ? (
          <form
            className="form"
            onSubmit={(e) => {
              submitReferralRequest({
                name: data?.name,
                email: data?.email,
                body,
              });
              e.preventDefault();
            }}
          >
            <label htmlFor="bodyId" className="form-label">
              {gt('Message')}
            </label>
            <textarea
              id="bodyId"
              name="body"
              className="form-control mb-1"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <button className="btn btn-primary px-4" type="submit">
              {gt('Sign me up')}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
};

const ReferralSuccessMessage: React.FC = () => {
  return (
    <div className="alert alert-success" role="alert">
      <h4 className="alert-heading">{gt('Thank You for Your Interest!')}</h4>
      <p>
        {gt(
          'Thank you for showing interest in the SweetProcess Referral Program! We’re delighted to see you’re considering partnering with us to share our product.'
        )}
      </p>
      <p>
        {gt(
          'We’re excited about the opportunity to work together and tailor a referral arrangement that suits both our needs. One of our team members will be in touch with you soon to discuss how we can make the most of your participation in our program.'
        )}
      </p>
      <p>
        {gt(
          'In the meantime, feel free to think about any specific ideas or strategies you might have for referrals. We’re keen to explore your suggestions and insights. Should you have any immediate questions or thoughts, please don’t hesitate to reach out to us.'
        )}
      </p>
      <p>
        {gt(
          'We’re looking forward to embarking on this collaborative journey with you!'
        )}
      </p>
    </div>
  );
};
